import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { PayPalButtons } from "@paypal/react-paypal-js";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const humaniseDetail = (detail) => {
  if (!detail || typeof detail !== "string") return null;
  if (detail.includes("PAYEE_ACCOUNT_RESTRICTED")) {
    return "Payments are temporarily disabled by the merchant. Please email Info@srimansolutions.com and we will confirm your pre-booking manually.";
  }
  if (detail.includes("CURRENCY_NOT_SUPPORTED")) {
    return "This currency is not supported by the merchant. Please contact us to complete the pre-booking.";
  }
  if (detail.includes("BILLING_ADDRESS_INVALID")) {
    return "Please check your billing address in PayPal and try again.";
  }
  if (detail.startsWith("PayPal: ")) return detail.replace("PayPal: ", "");
  return detail;
};

export const PayPalCheckoutButton = ({ productId, productName, testId }) => {
  const navigate = useNavigate();
  const errorShownRef = useRef(false);

  const showError = (detail, fallback) => {
    const humane =
      humaniseDetail(detail) ||
      fallback ||
      "PayPal checkout could not be started.";
    errorShownRef.current = true;
    toast.error(humane, { duration: 8000 });
  };

  const createOrder = async () => {
    errorShownRef.current = false;
    try {
      const { data } = await axios.post(`${API}/payments/create-order`, {
        product_id: productId,
      });
      return data.order_id;
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message;
      showError(detail, "Unable to start PayPal checkout right now.");
      throw err;
    }
  };

  const onApprove = async (data) => {
    try {
      await axios.post(`${API}/payments/capture-order/${data.orderID}`);
      toast.success("Payment received.");
      navigate(`/payment/success?order_id=${encodeURIComponent(data.orderID)}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      showError(
        detail,
        "Payment capture failed. Please email us your order id.",
      );
      navigate(`/payment/success?order_id=${encodeURIComponent(data.orderID)}`);
    }
  };

  const onError = (err) => {
    // eslint-disable-next-line no-console
    console.error("PayPal error", err);
    // Only surface a message if we haven't already shown a more specific one
    if (errorShownRef.current) return;
    toast.error(
      "PayPal is unavailable right now. Please email Info@srimansolutions.com to complete the pre-booking.",
      { duration: 8000 },
    );
  };

  const onCancel = () => {
    if (errorShownRef.current) return; // don't overwrite an error message with a cancel toast
    navigate(`/payment/cancel?product=${encodeURIComponent(productId)}`);
  };

  return (
    <div data-testid={testId} className="w-full">
      <PayPalButtons
        style={{
          layout: "vertical",
          shape: "rect",
          color: "gold",
          label: "paypal",
          height: 48,
        }}
        createOrder={createOrder}
        onApprove={onApprove}
        onError={onError}
        onCancel={onCancel}
      />
      <p className="mt-2 text-[11px] text-slate-500 font-mono tracking-wider">
        Secure checkout for {productName} · powered by PayPal
      </p>
    </div>
  );
};

export default PayPalCheckoutButton;
